"use client";

// Grade Model Feedback — in-session store, v0.2.7.
//
// The real-world problem: an AM or Tim looks at a graded script and says
// "this should be a B+, not an A-, here's why" — and right now that
// correction has nowhere to go. This module is the capture mechanism: every
// disagreement becomes a row in a running log instead of being silently
// lost. It does NOT change gradeScript()'s output — this is human feedback
// ABOUT the grader, collected for a future retraining/tuning pass, not a
// live override of any individual grade.
//
// SEAM NOTE: this is a plain in-memory array + listener Set, same pattern
// as ironside-script-store.ts (useSyncExternalStore-compatible), and state
// resets on a hard reload. The data shape below is written to be the exact
// shape of the real table this becomes first when a backend exists —
// think `grader_feedback` in Postgres, one row per submission, immutable
// except for `status`. No field here is a UI convenience that wouldn't
// belong in that schema.

import { persist, isConfirmed } from "./store-persist";
import { sameData } from "./same-data";

export type FeedbackStatus = "new" | "reviewed" | "actioned";

export interface GraderFeedbackEntry {
  id: string;
  submittedAt: string; // ISO timestamp
  submittedByLabel: string; // e.g. "AM: Acsah", "Creator: Jay Crotty" — from usePersona().label at submit time
  scriptId: string;
  scriptTitle: string;
  creatorName: string;
  brandName: string;
  algorithmGrade: number; // the 0-100 score the algorithm gave at submission time, v0.3.0
  /** The grading run this argues with.
   *
   *  THIS IS WHAT MAKES A CORRECTION READABLE LATER. Without it an entry says
   *  "this should be a B+" about a script that has since been rewritten, and
   *  there is no way to see the text the author was actually looking at. With
   *  it the run holds its own frozen snapshot, so later edits are irrelevant by
   *  construction — no drift, no anchor to relocate.
   *
   *  Optional because it has to be: entries filed before this existed have no
   *  run to point at and never will, and neither do corrections on a grade that
   *  predates runId and hasn't been regraded or backfilled. Absent means "no
   *  snapshot", which the console renders plainly rather than treating as a bug.
   *
   *  `algorithmGrade` stays alongside it, deliberately redundant: a frozen copy
   *  of the number is a cheap check that an entry and the run it names agree. */
  gradeRunId?: string;
  whatsWrong: string; // one-line disagreement, e.g. "This should be a B+, not an A-"
  whyAndInstead: string; // full reasoning: what the algorithm missed, what the correct check should be
  status: FeedbackStatus;
}

const entries: GraderFeedbackEntry[] = [];
const listeners = new Set<() => void>();
let version = 0;

function notify() {
  version++;
  listeners.forEach((l) => l());
}

export interface SubmitFeedbackInput {
  submittedByLabel: string;
  scriptId: string;
  scriptTitle: string;
  creatorName: string;
  brandName: string;
  algorithmGrade: number;
  gradeRunId?: string;
  whatsWrong: string;
  whyAndInstead: string;
}

export function submitGraderFeedback(input: SubmitFeedbackInput): GraderFeedbackEntry {
  const entry: GraderFeedbackEntry = {
    id: `gf-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    submittedAt: new Date().toISOString(),
    status: "new",
    ...input,
  };
  entries.unshift(entry); // newest first
  persist("grader_feedback", entry.id, entry);
  notify();
  return entry;
}

export function setFeedbackStatus(id: string, status: FeedbackStatus): void {
  const entry = entries.find((e) => e.id === id);
  if (!entry) return;
  entry.status = status;
  persist("grader_feedback", entry.id, entry);
  notify();
}

export function getGraderFeedback(): GraderFeedbackEntry[] {
  return entries;
}

export function subscribeGraderFeedback(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getGraderFeedbackVersion(): number {
  return version;
}

// ─── Hydration from the server ───────────────────────────────────────────────
// Array-backed: upsert by id, then re-sort newest-first by the ISO submittedAt.
// Skip keys with an unconfirmed local write; bump version only when a row's
// content changed (lib/same-data.ts), so an idle poll notifies nobody. A key
// deleted on the server leaves the list.
export function applyServerRows(
  rows: { kind: string; id: string; data: unknown }[],
  deleted: { kind: string; id: string }[] = [],
): void {
  let changed = false;
  for (const d of deleted) {
    if (d.kind !== "grader_feedback" || !isConfirmed(d.kind, d.id, null)) continue;
    const idx = entries.findIndex((e) => e.id === d.id);
    if (idx >= 0) {
      entries.splice(idx, 1);
      changed = true;
    }
  }
  for (const r of rows) {
    if (r.kind !== "grader_feedback") continue;
    if (!isConfirmed(r.kind, r.id, r.data)) continue;
    const incoming = r.data as GraderFeedbackEntry;
    const idx = entries.findIndex((e) => e.id === incoming.id);
    if (idx >= 0 && sameData(entries[idx], incoming)) continue; // nothing new: no re-sort, no notify
    if (idx >= 0) entries[idx] = incoming;
    else entries.push(incoming);
    changed = true;
  }
  if (changed) {
    entries.sort((a, b) => b.submittedAt.localeCompare(a.submittedAt)); // newest first
    notify();
  }
}
